/**
 * Timers (§14.3).
 *
 * - `LobbyTimersPanel`: everyone sees the timers; the host picks a preset (Quick, Standard,
 *   Relaxed, Large group) or steps any timer within its allowed range. Changes go out as
 *   `setTimers` after a short pause.
 * - `InGameTimers`: the host's menu during play: add or remove time from the current phase
 *   (showing the resulting time; the engine keeps at least 15 s), end it now, and change the
 *   upcoming phases' timers. Every change is announced to everyone by the engine.
 */
import type { TimerConfig } from "@handshake/config";
import {
  formatDuration,
  MIN_REMAINING_SECONDS,
  TIMER_KEYS,
  TIMER_LABELS,
  TIMER_PRESET_IDS,
  TIMER_PRESET_LABELS,
  TIMER_RANGES,
  timerPreset,
  type TimerKey,
  type TimerPresetId,
} from "@handshake/config/catalog";
import { useState } from "react";
import { HoldButton } from "../../components/HoldButton.tsx";
import { Stepper } from "../../components/Stepper.tsx";
import { useDraft } from "../../lib/draft.ts";
import { send } from "../../lib/store.ts";
import { formatClock, remainingMs, useServerNow } from "../../lib/time.ts";
import type { PhaseView } from "../../lib/types.ts";

/** Step for a timer's stepper: 1 s for card choice, 5 s for defence, else 15 s or 30 s. */
export function timerStep(key: TimerKey): number {
  if (key === "cardChoice") return 1;
  if (key === "defencePerNominee") return 5;
  return TIMER_RANGES[key].max <= 180 ? 15 : 30;
}

/** The preset whose timers equal these, if any. */
export function matchingPreset(timers: TimerConfig, players: number): TimerPresetId | null {
  const same = (id: TimerPresetId) => {
    const p = timerPreset(id, players);
    return TIMER_KEYS.every((k) => p[k] === timers[k]);
  };
  return TIMER_PRESET_IDS.find(same) ?? null;
}

function rangeText(key: TimerKey): string {
  const r = TIMER_RANGES[key];
  return `${formatDuration(r.min)}–${formatDuration(r.max)}`;
}

/** Steppers for every timer, each within its allowed range. */
export function TimerEditor({
  timers,
  onChange,
  idPrefix = "timer",
}: {
  timers: TimerConfig;
  onChange: (timers: TimerConfig) => void;
  idPrefix?: string;
}) {
  return (
    <ul className="list">
      {TIMER_KEYS.map((k) => (
        <li key={k} style={{ flexWrap: "wrap" }}>
          <span className="grow">
            {TIMER_LABELS[k]}
            <span className="faint" style={{ display: "block" }}>
              {rangeText(k)}
            </span>
          </span>
          <Stepper
            value={timers[k]}
            min={TIMER_RANGES[k].min}
            max={TIMER_RANGES[k].max}
            step={timerStep(k)}
            label={TIMER_LABELS[k]}
            format={formatDuration}
            onChange={(v) => onChange({ ...timers, [k]: v })}
            testId={`${idPrefix}-${k}`}
          />
        </li>
      ))}
    </ul>
  );
}

function TimerList({ timers }: { timers: TimerConfig }) {
  return (
    <ul className="list">
      {TIMER_KEYS.map((k) => (
        <li key={k}>
          <span className="grow muted">{TIMER_LABELS[k]}</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }} data-testid={`timer-value-${k}`}>
            {formatDuration(timers[k])}
          </span>
        </li>
      ))}
    </ul>
  );
}

async function sendTimers(timers: TimerConfig, fromNextPhase?: boolean): Promise<boolean> {
  const ack = await send(
    fromNextPhase === undefined
      ? { type: "setTimers", timers }
      : { type: "setTimers", timers, fromNextPhase },
  );
  return ack.ok;
}

export function LobbyTimersPanel({
  timers,
  isHost,
  players,
}: {
  timers: TimerConfig;
  isHost: boolean;
  /** The player count the presets are computed for. */
  players: number;
}) {
  const [draft, setDraft] = useDraft(timers, (t) => sendTimers(t));
  const shown = isHost ? draft : timers;
  const preset = matchingPreset(shown, players);
  return (
    <section className="panel stack" data-testid="timers-panel">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>
          Timers
        </h3>
        <span className="tag plain" data-testid="timer-preset">
          {preset ? TIMER_PRESET_LABELS[preset] : "Custom"}
        </span>
      </div>
      {isHost ? (
        <>
          <div className="preset-grid" role="group" aria-label="Timer presets">
            {TIMER_PRESET_IDS.map((id) => (
              <button
                key={id}
                type="button"
                className="btn small"
                aria-pressed={preset === id}
                data-testid={`timer-preset-${id}`}
                onClick={() => setDraft(timerPreset(id, players))}
              >
                {TIMER_PRESET_LABELS[id]}
              </button>
            ))}
          </div>
          <TimerEditor timers={draft} onChange={setDraft} />
        </>
      ) : (
        <TimerList timers={timers} />
      )}
    </section>
  );
}

/** Remaining time after adding `deltaMs`, never below 15 s (or below what is left). */
export function afterAdjust(remaining: number, deltaMs: number): number {
  const floor = Math.min(remaining, MIN_REMAINING_SECONDS * 1000);
  return Math.max(remaining + deltaMs, floor);
}

const DELTAS = [
  { ms: -60_000, label: "−1 min" },
  { ms: -30_000, label: "−30 s" },
  { ms: 30_000, label: "+30 s" },
  { ms: 60_000, label: "+1 min" },
] as const;

/** Host, during play: the current phase's time, and the upcoming phases' timers. */
export function InGameTimers({
  phase,
  timers,
  onDone,
}: {
  phase: PhaseView;
  timers: TimerConfig;
  onDone: () => void;
}) {
  const now = useServerNow(500);
  const left = remainingMs(phase, now);
  const [next, setNext] = useState<TimerConfig>(timers);
  const changed = TIMER_KEYS.some((k) => next[k] !== timers[k]);
  return (
    <div className="stack">
      <div data-testid="current-phase-time">
        <div className="row">
          <span className="grow muted">This phase</span>
          <strong className="clock" style={{ fontSize: "1.2rem" }}>
            {left === null ? "no timer" : formatClock(left)}
          </strong>
        </div>
        <div className="delta-grid">
          {DELTAS.map((d) => {
            const result = left === null ? null : afterAdjust(left, d.ms);
            const noop = result === null || Math.abs(result - left!) < 1000;
            return (
              <button
                key={d.ms}
                type="button"
                className="btn small"
                disabled={noop}
                data-testid={`adjust-${d.ms}`}
                onClick={() => void send({ type: "adjustPhaseTime", deltaMs: d.ms })}
              >
                <span>{d.label}</span>
                {result !== null ? <span className="faint">→ {formatClock(result)}</span> : null}
              </button>
            );
          })}
        </div>
        <p className="faint" style={{ margin: "0.4rem 0 0" }}>
          At least {MIN_REMAINING_SECONDS} seconds always remain.
        </p>
      </div>
      <HoldButton
        className="btn block"
        testId="end-phase"
        disabled={left === null}
        onConfirm={() => {
          void send({ type: "endPhaseNow" });
          onDone();
        }}
      >
        Hold to end this phase now
      </HoldButton>
      <details className="panel" data-testid="upcoming-timers">
        <summary style={{ cursor: "pointer", minHeight: "2.5rem" }}>
          <strong>Upcoming phases</strong>
          <span className="faint"> · change timers from the next phase</span>
        </summary>
        <TimerEditor timers={next} onChange={setNext} idPrefix="next-timer" />
        <div className="stack" style={{ marginTop: "0.75rem" }}>
          <button
            type="button"
            className="btn primary block"
            disabled={!changed}
            data-testid="apply-timers"
            onClick={() => {
              void sendTimers(next, true).then((ok) => ok && onDone());
            }}
          >
            Apply from the next phase
          </button>
          <button
            type="button"
            className="btn block"
            disabled={!changed}
            onClick={() => {
              void sendTimers(next, false).then((ok) => ok && onDone());
            }}
          >
            Apply now, this phase too
          </button>
        </div>
      </details>
    </div>
  );
}
